import { useEffect, useState } from 'react';

/** Cross-browser fullscreen element accessor (WebKit fallback). */
type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
};

export function getFullscreenElement(): Element | null {
  if (typeof document === 'undefined') return null;
  const d = document as FsDocument;
  return document.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

/**
 * useIsFullscreen — true while the referenced element is the document's
 * fullscreen element. Lets chart panels drop their fixed pixel height and fill
 * the screen when expanded, instead of staying letterboxed (FXC-9004): the
 * panels' own `fullscreen` prop was never wired up because the FullscreenButton
 * owned that state privately.
 */
export function useIsFullscreen(
  targetRef: React.RefObject<HTMLElement | null>,
): boolean {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => {
      const fs = getFullscreenElement();
      setIsFullscreen(fs !== null && fs === targetRef.current);
    };
    handler();
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
    };
  }, [targetRef]);

  return isFullscreen;
}
